import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Upload, Download, Bell } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import AnnouncementDialog from '../AnnouncementDialog';

interface ScheduleManagementProps {
  sessions: any[];
  onNewSession: () => void;
}

const ScheduleManagement: React.FC<ScheduleManagementProps> = ({
  sessions,
  onNewSession
}) => {
  useUiLanguage();
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);

  const handleExportSchedule = () => {
    const scheduleData = JSON.stringify(sessions, null, 2);
    const blob = new Blob([scheduleData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `training-schedule-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: uiText("Schedule Exported"),
      description: uiText("Training schedule has been exported successfully.")
    });
  };

  const handleImportSchedule = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const importedData = JSON.parse(e.target?.result as string);
            console.log('Imported schedule data:', importedData);
            toast({
              title: uiText("Schedule Imported"),
              description: uiText("Training schedule has been imported successfully.")
            });
          } catch (error) {
            toast({
              title: uiText("Import Failed"),
              description: uiText("Failed to import schedule. Please check the file format."),
              variant: "destructive"
            });
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{uiText("Schedule Management")}</CardTitle>
          <CardDescription>{uiText("Administrative tools for managing the training schedule")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              className="bg-red-900 hover:bg-red-800"
              onClick={onNewSession}
            >
              <Plus className="h-4 w-4 mr-2" />
              {uiText("Add New Session")}</Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-2"
              onClick={() => setAnnouncementDialogOpen(true)}
            >
              <Bell className="h-4 w-4" />
              {uiText("Send Announcement")}</Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-2"
              onClick={handleImportSchedule}
            >
              <Upload className="h-4 w-4" />
              {uiText("Import Schedule")}</Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-2"
              onClick={handleExportSchedule}
            >
              <Download className="h-4 w-4" />
              {uiText("Export Schedule")}</Button>
          </div>
        </CardContent>
      </Card>

      <AnnouncementDialog
        open={announcementDialogOpen}
        onOpenChange={setAnnouncementDialogOpen}
      />
    </>
  );
};

export default ScheduleManagement;
